import * as THREE from 'three';
import { AppState } from '../core/constants.js';

function makePlaneGeometry(index) {
  const width = 0.95 + (index % 3) * 0.15;
  const height = width * 0.72;
  return new THREE.PlaneGeometry(width, height);
}

function generateClosedPosition(index, total) {
  const ratio = total === 1 ? 0.5 : index / (total - 1);
  const y = 0.9 + ratio * 4.8;
  const radius = 0.5 + (1 - ratio) * 1.2;
  const angle = index * 2.399963229728653;
  return new THREE.Vector3(Math.cos(angle) * radius, y, Math.sin(angle) * radius);
}

function generateDispersedPosition(index) {
  const radius = 3.2 + (index % 7) * 0.48 + Math.random() * 1.9;
  const theta = index * 0.73 + Math.random() * 0.4;
  const y = 1 + (index % 9) * 0.55;
  return new THREE.Vector3(Math.cos(theta) * radius, y, Math.sin(theta) * radius);
}

export class PhotoCloud {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.position.set(0, 0, 0);
    this.scene.add(this.group);

    this.loader = new THREE.TextureLoader();
    this.raycaster = new THREE.Raycaster();

    this.items = [];
    this.focusAnchor = new THREE.Vector3(0, 3.2, 2.3);
  }

  async setFiles(files) {
    this.clear();
    const selectedFiles = Array.from(files);

    const urls = selectedFiles.map((file) => URL.createObjectURL(file));

    try {
      const textures = await Promise.all(urls.map((url) => this.loader.loadAsync(url)));

      textures.forEach((texture, index) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        const geometry = makePlaneGeometry(index);
        const material = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          opacity: 0.92,
          side: THREE.DoubleSide
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData.photoIndex = index;
        this.group.add(mesh);

        this.items.push({
          mesh,
          texture,
          objectUrl: urls[index],
          closedPos: generateClosedPosition(index, textures.length),
          dispersedPos: generateDispersedPosition(index),
          velocitySeed: {
            speed: 0.5 + Math.random() * 1.1,
            phase: Math.random() * Math.PI * 2,
            amp: 0.08 + Math.random() * 0.2
          }
        });
      });
    } catch (error) {
      this.clear();
      urls.forEach((url) => URL.revokeObjectURL(url));
      throw error;
    }
  }

  clear() {
    this.items.forEach((item) => {
      this.group.remove(item.mesh);
      item.mesh.geometry.dispose();
      item.mesh.material.dispose();
      item.texture.dispose();
      URL.revokeObjectURL(item.objectUrl);
    });
    this.items = [];
  }

  count() {
    return this.items.length;
  }

  pickPhoto(camera, ndcX, ndcY) {
    if (this.items.length === 0) {
      return null;
    }

    this.raycaster.setFromCamera({ x: ndcX, y: ndcY }, camera);
    const intersects = this.raycaster.intersectObjects(
      this.items.map((item) => item.mesh),
      false
    );

    if (intersects.length === 0) {
      return null;
    }

    return intersects[0].object.userData.photoIndex;
  }

  update(state, focusedPhotoIndex, camera, elapsedSeconds) {
    const inFocus = state === AppState.PHOTO_FOCUS && Number.isInteger(focusedPhotoIndex);

    this.items.forEach((item, index) => {
      let targetPos;
      let targetScale = 1;
      let targetOpacity = 0.92;

      if (inFocus && index === focusedPhotoIndex) {
        targetPos = this.focusAnchor;
        targetScale = 2.35;
        targetOpacity = 1;
      } else if (inFocus && index !== focusedPhotoIndex) {
        targetPos = item.dispersedPos;
        targetScale = 0.82;
        targetOpacity = 0.15;
      } else if (state === AppState.CLOSED) {
        targetPos = item.closedPos;
      } else {
        const drift = Math.sin(elapsedSeconds * item.velocitySeed.speed + item.velocitySeed.phase);
        const liftedPos = new THREE.Vector3(
          item.dispersedPos.x + drift * item.velocitySeed.amp,
          item.dispersedPos.y + Math.cos(elapsedSeconds * item.velocitySeed.speed * 0.7) * item.velocitySeed.amp,
          item.dispersedPos.z + Math.sin(elapsedSeconds * item.velocitySeed.speed * 0.9) * item.velocitySeed.amp
        );
        targetPos = liftedPos;
      }

      item.mesh.position.lerp(targetPos, 0.11);
      const currentScale = item.mesh.scale.x;
      const nextScale = currentScale + (targetScale - currentScale) * 0.15;
      item.mesh.scale.set(nextScale, nextScale, nextScale);
      item.mesh.material.opacity += (targetOpacity - item.mesh.material.opacity) * 0.2;
      item.mesh.lookAt(camera.position);
    });
  }

  dispose() {
    this.clear();
    this.scene.remove(this.group);
  }
}
